import { t as __t } from "i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import DashboardHeader from "../../../components/header/DashboardHeader";
import DashboardEmployerSidebar from "../../../components/header/DashboardSidebar";
import MobileMenu from "../../../components/header/MobileMenu";
import CopyrightFooter from "../../../components/footer/employers/CopyrightFooter";
import SwalAlert from "../../../components/common/alert";

import { callGetLifeInsuranceClaiming } from "../../../api/insurances/service";
import { callGetApproveContract } from "../../../api/contract/service";
import {
  decodeBase64,
  encodeBase64,
  dateToString,
  FormatCurrencys,
} from "../../../utils/commonUtil";

import "../../../components/i18n/lang";
import "./claim-tracking.css";

const CLAIM_STATUS = {
  0: {
    labelKey: "claimTracking.statuses.notApproved.label",
    descriptionKey: "claimTracking.statuses.notApproved.description",
    icon: "fa-times-circle",
    tone: "danger",
  },
  1: {
    labelKey: "claimTracking.statuses.underReview.label",
    descriptionKey: "claimTracking.statuses.underReview.description",
    icon: "fa-search",
    tone: "warning",
  },
  2: {
    labelKey: "claimTracking.statuses.approved.label",
    descriptionKey: "claimTracking.statuses.approved.description",
    icon: "fa-check-circle",
    tone: "success",
  },
  3: {
    labelKey: "claimTracking.statuses.completed.label",
    descriptionKey: "claimTracking.statuses.completed.description",
    icon: "fa-wallet",
    tone: "success",
  },
};

const ClaimTracking = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null);
  const [claim, setClaim] = useState(null);

  const handleBack = () => {
    navigate(-1);
  };

  const handleNewClaim = () => {
    const contractNumber =
      contract?.contractNumber ||
      contract?.contract_number ||
      claim?.contractNumber ||
      claim?.contract_number;

    if (!contractNumber) {
      SwalAlert.error(
        __t("uiText.text260"),
        __t("uiText.text261")
      );
      return;
    }

    navigate(
      `/admin/contractControl/lifeInsurance/clame/${encodeBase64(contractNumber)}`,
      {
        state: {
          fromClaimTracking: true,
          previousClaimId: claim?.id || null,
          contractNumber,
        },
      }
    );
  };

  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.id]);

  const initialize = async () => {
    if (!params?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const decoded = decodeBase64(params.id);
      const payload = JSON.parse(decoded);
      const requestId = payload?.requestId || payload?.id || null;

      if (requestId) {
        await loadClaimDetail(requestId);
        return;
      }

      if (payload?.contractNumber) {
        await resolveClaimByContract(payload.contractNumber);
        return;
      }

      showNotFound();
    } catch (error) {
      console.error("Claim tracking parameter error:", error);
      SwalAlert.error(
        __t("uiText.text023"),
        __t("uiText.text262")
      );
      setLoading(false);
    }
  };

  const resolveClaimByContract = async (contractNumber) => {
    try {
      const rsp = await dispatch(
        callGetLifeInsuranceClaiming({
          page: 1,
          maxResult: 20,
          contractNumber,
          status: "",
        })
      );

      if (rsp?.payload?.responseCode !== "00000") {
        showNotFound(rsp?.payload?.responseDescription);
        return;
      }

      const list = Array.isArray(rsp?.payload?.data?.result)
        ? rsp.payload.data.result
        : [];
      const item =
        list.find((x) => x?.contractNumber === contractNumber) || list[0];

      if (!item?.id) {
        showNotFound();
        return;
      }

      await loadClaimDetail(item.id);
    } catch (error) {
      console.error("Resolve life claim error:", error);
      showNotFound();
    }
  };

  const loadClaimDetail = async (requestId) => {
    try {
      const rsp = await dispatch(callGetApproveContract({ requestId }));

      if (rsp?.payload?.responseCode !== "00000") {
        showNotFound(rsp?.payload?.responseDescription);
        return;
      }

      const data = rsp?.payload?.data || {};
      console.log("Claim tracking detail data:", data);
      setContract(data?.contract || null);
      setClaim(data?.requestClaim || null);
    } catch (error) {
      console.error("Load claim tracking detail error:", error);
      SwalAlert.error(
        __t("uiText.text027"),
        __t("uiText.text024")
      );
    } finally {
      setLoading(false);
    }
  };

  const showNotFound = (message) => {
    SwalAlert.error(
      __t("uiText.text029"),
      message || __t("uiText.text030")
    );
    setLoading(false);
  };

  const rawStatus = Number(claim?.status);
  const status = [0, 1, 2, 3].includes(rawStatus) ? rawStatus : 1;
  const statusInfo = CLAIM_STATUS[status];

  const claimSteps = useMemo(() => {
    const rejected = status === 0;
    const submittedDate = claim?.carriredDate || claim?.createdAt;
    const approvedDate = claim?.updatedAt;

    return [
      {
        id: 1,
        title: __t("uiText.text069"),
        description: submittedDate
          ? __t("uiText.text263", { value1: formatDate(submittedDate) })
          : __t("uiText.text071"),
        icon: "fa-file-signature",
        state: "done",
      },
      {
        id: 2,
        title: rejected ? __t("uiText.text072") : __t("uiText.text264"),
        description: rejected
          ? claim?.descriptionUser || claim?.description || __t("uiText.text265")
          : status === 1
            ? __t("uiText.text266")
            : __t("uiText.text267"),
        icon: rejected ? "fa-times" : "fa-search",
        state: rejected ? "rejected" : status === 1 ? "current" : "done",
      },
      {
        id: 3,
        title: __t("uiText.text077"),
        description:
          status >= 2
            ? approvedDate
              ? __t("uiText.text268", { value1: formatDate(approvedDate) })
              : __t("uiText.text079")
            : __t("uiText.text080"),
        icon: "fa-check",
        state: rejected ? "disabled" : status === 2 ? "current" : status > 2 ? "done" : "disabled",
      },
      {
        id: 4,
        title: __t("uiText.text269"),
        description:
          status === 3
            ? __t("uiText.text270")
            : __t("uiText.text083"),
        icon: "fa-wallet",
        state: rejected ? "disabled" : status === 3 ? "done" : "disabled",
      },
    ];
  }, [claim, status]);

  const insuredName = [
    contract?.firstName,
    contract?.lastName,
  ]
    .filter(Boolean)
    .join(" ") || contract?.fullName || "-";

  return (
    <div className="page-wrapper dashboard">
      <span className="header-span"></span>
      <DashboardHeader />
      <MobileMenu />
      <DashboardEmployerSidebar />

      <section className="user-dashboard claim-track-page">
        <div className="dashboard-outer">
          <div className="claim-track-heading">
            <div>
              <button
                type="button"
                className="claim-track-back"
                onClick={handleBack}
              >
                <i className="fas fa-arrow-left" />
                <span>{__t("uiText.text271")}</span>
              </button>
              <h4>{__t("uiText.text272")}</h4>
              <p>
                {__t("uiText.text273")}</p>
            </div>
          </div>

          {loading ? (
            <TrackingSkeleton />
          ) : !claim || !contract ? (
            <div className="claim-track-empty">
              <div className="claim-track-empty-icon">
                <i className="fas fa-file-alt" />
              </div>
              <h5>{__t("uiText.text274")}</h5>
              <p>{__t("uiText.text275")}</p>
            </div>
          ) : (
            <>
              <section className={`claim-track-hero ${statusInfo.tone}`}>
                <div className="claim-track-hero-main">
                  <div className="claim-track-status-icon">
                    <i className={`fas ${statusInfo.icon}`} />
                  </div>
                  <div className="claim-track-status-copy">
                    <span className="claim-track-eyebrow">{__t("uiText.text276")}</span>
                    <h3>{t(statusInfo.labelKey)}</h3>
                    <p>{t(statusInfo.descriptionKey)}</p>
                  </div>
                </div>

                <div className="claim-track-hero-meta">
                  <MetaItem
                    label={t("uiLabels.contractNumber")}
                    value={contract?.contractNumber || "-"}
                    icon="fa-file-contract"
                  />
                  <MetaItem
                    label={t("uiLabels.claimRequestNumber")}
                    value={claim?.id || "-"}
                    icon="fa-hashtag"
                  />
                  <MetaItem
                    label={t("uiLabels.claimSubmittedDate")}
                    value={formatDate(claim?.carriredDate || claim?.createdAt)}
                    icon="fa-calendar-alt"
                  />
                </div>
              </section>

              {status === 0 && (
                <section className="claim-track-reject-box">
                  <div className="claim-track-reject-main">
                    <div className="claim-track-reject-icon">
                      <i className="fas fa-exclamation-circle" />
                    </div>
                    <div className="claim-track-reject-copy">
                      <span>{__t("uiText.text277")}</span>
                      <strong>
                        {claim?.descriptionUser ||
                          claim?.description ||
                          __t("uiText.text278")}
                      </strong>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="claim-track-new-claim-btn"
                    onClick={handleNewClaim}
                  >
                    <i className="fas fa-plus-circle" />
                    <span>{__t("uiText.text279")}</span>
                  </button>
                </section>
              )}

              <section className="claim-track-card claim-track-progress-card">
                <SectionTitle
                  icon="fa-route"
                  title={__t("uiText.text280")}
                  description={__t("uiText.text281")}
                />
                <div className="claim-track-progress">
                  {claimSteps.map((step, index) => (
                    <div
                      key={step.id}
                      className={`claim-track-step ${step.state}`}
                    >
                      {index < claimSteps.length - 1 && (
                        <div className="claim-track-step-line" />
                      )}
                      <div className="claim-track-step-icon">
                        <i className={`fas ${step.icon}`} />
                      </div>
                      <div className="claim-track-step-copy">
                        <span>{__t("uiText.text090")}{step.id}</span>
                        <strong>{step.title}</strong>
                        <p>{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <div className="row g-4 claim-track-content-row">
                <div className="col-xl-8 col-lg-7">
                  <section className="claim-track-card h-100">
                    <SectionTitle
                      icon="fa-file-medical"
                      title={__t("uiText.text282")}
                      description={__t("uiText.text283")}
                    />

                    <div className="claim-track-info-grid">
                      <InfoItem
                        label={t("uiLabels.claimCauseType")}
                        value={
                          claim?.claimCause !== null && claim?.claimCause !== undefined
                            ? safeTranslate(t, `causeList.${claim.claimCause}`, claim.claimCause)
                            : "-"
                        }
                        icon="fa-notes-medical"
                      />
                      <InfoItem
                        label={t("uiLabels.incidentOrDeathDate")}
                        value={formatDate(claim?.deadDate)}
                        icon="fa-calendar-day"
                      />
                      <InfoItem
                        label={t("uiLabels.numberOfDays")}
                        value={claim?.amountDay ?? "-"}
                        icon="fa-calendar-check"
                      />
                      <InfoItem
                        label={t("uiLabels.lastUpdated")}
                        value={formatDateTime(claim?.updatedAt)}
                        icon="fa-clock"
                      />
                      <InfoItem
                        label={t("uiLabels.detailsNotes")}
                        value={claim?.description || "-"}
                        icon="fa-align-left"
                        full
                      />
                    </div>
                  </section>
                </div>

                <div className="col-xl-4 col-lg-5">
                  <section className="claim-track-card claim-track-money-card h-100">
                    <SectionTitle
                      icon="fa-coins"
                      title={__t("uiText.text284")}
                      description={__t("uiText.text285")}
                    />
                    <MoneyRow
                      label={t("uiLabels.claimMoney")}
                      value={claim?.claimAmount}
                      primary
                    />
                    <MoneyRow
                      label={t("uiLabels.paidTotal")}
                      value={claim?.totalPaidAmount}
                      isMoney={false}
                      title={__t("uiText.text286")}
                    />
                    <MoneyRow
                      label={t("uiLabels.amountToCustomer")}
                      value={claim?.refundAfterFee}
                    />
                    <MoneyRow
                      label={t("uiLabels.customerPaidTotal")}
                      value={claim?.overPaidAmount}
                      accent
                    />
                  </section>
                </div>
              </div>

              <div className="row g-4 mt-0">
                <div className="col-xl-8 col-lg-7">
                  <section className="claim-track-card">
                    <SectionTitle
                      icon="fa-user-shield"
                      title={__t("uiText.text287")}
                      description={__t("uiText.text288")}
                    />
                    <div className="claim-track-profile">
                      <div className="claim-track-avatar">
                        <i className="fas fa-user" />
                      </div>
                      <div className="claim-track-profile-name">
                        <span>{__t("uiText.text289")}</span>
                        <strong>{insuredName}</strong>
                        <p>{contract?.phone || "-"}</p>
                      </div>
                    </div>

                    <div className="claim-track-detail-grid">
                      <Detail label={t("uiLabels.contractNumber")} value={contract?.contractNumber} />
                      <Detail label={t("uiLabels.agentCode")} value={contract?.agentCode} />
                      <Detail label={t("uiLabels.package")} value={contract?.ageRange ? __t("uiText.text290", { value1: contract.ageRange }) : "-"} />
                      <Detail label={t("uiLabels.monthlyPremium")} value={money(contract?.payPerMonth)} />
                      <Detail label={t("uiLabels.signedDate")} value={formatDate(contract?.signedDate)} />
                      <Detail label={t("uiLabels.lastPaymentDate")} value={contract?.lastPay} />
                      <Detail label={t("uiLabels.phone")} value={contract?.phone} />
                      <Detail label={t("uiLabels.address")} value={contract?.address} />
                    </div>
                  </section>
                </div>

                <div className="col-xl-4 col-lg-5">
                  <section className="claim-track-card claim-track-bank-card">
                    <SectionTitle
                      icon="fa-university"
                      title={__t("uiText.text061")}
                      description={__t("uiText.text062")}
                    />
                    <div className="claim-track-bank-icon">
                      <i className="fas fa-university" />
                    </div>
                    <Detail
                      label={t("uiLabels.bank")}
                      value={
                        claim?.bankType
                          ? safeTranslate(t, `bank.${claim.bankType}`, claim.bankType)
                          : "-"
                      }
                    />
                    <Detail label={t("uiLabels.accountName")} value={claim?.bankAccName} />
                    <Detail label={t("uiLabels.accountNumber")} value={claim?.bankAcc} emphasize />
                  </section>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      <CopyrightFooter />
    </div>
  );
};

const SectionTitle = ({ icon, title, description }) => (
  <div className="claim-track-section-title">
    <div className="claim-track-section-icon">
      <i className={`fas ${icon}`} />
    </div>
    <div>
      <h5>{title}</h5>
      <p>{description}</p>
    </div>
  </div>
);

const MetaItem = ({ icon, label, value }) => (
  <div className="claim-track-meta-item">
    <div className="claim-track-meta-icon">
      <i className={`fas ${icon}`} />
    </div>
    <div>
      <span>{label}</span>
      <strong>{value || "-"}</strong>
    </div>
  </div>
);

const InfoItem = ({ icon, label, value, full = false }) => (
  <div className={`claim-track-info-item ${full ? "full" : ""}`}>
    <div className="claim-track-info-icon">
      <i className={`fas ${icon}`} />
    </div>
    <div>
      <span>{label}</span>
      <strong>{value ?? "-"}</strong>
    </div>
  </div>
);

const MoneyRow = ({ label, value, primary = false, accent = false, isMoney = true, title }) => (
  <div
    className={`claim-track-money-row ${primary ? "primary" : ""} ${accent ? "accent" : ""
      }`}
  >
    <span>{label}</span>
    <strong>{money(value, isMoney, title)}</strong>
  </div>
);

const Detail = ({ label, value, emphasize = false }) => (
  <div className={`claim-track-detail ${emphasize ? "emphasize" : ""}`}>
    <span>{label}</span>
    <strong>{value || "-"}</strong>
  </div>
);

const TrackingSkeleton = () => (
  <div className="claim-track-skeleton-wrap">
    <div className="claim-track-skeleton hero" />
    <div className="claim-track-skeleton progress" />
    <div className="row g-4">
      <div className="col-lg-8">
        <div className="claim-track-skeleton block" />
      </div>
      <div className="col-lg-4">
        <div className="claim-track-skeleton block" />
      </div>
    </div>
  </div>
);

const money = (value, isMoney = true, title = "") => {
  if (value === null || value === undefined || value === "") return "-";
  return `${FormatCurrencys(Number(value || 0))} ${isMoney ? __t("uiText.text291") : title}`;
};

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return dateToString(value, "DD/MM/YYYY");
  } catch (error) {
    return value;
  }
};

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    return dateToString(value, "DD/MM/YYYY HH:mm");
  } catch (error) {
    return value;
  }
};

const safeTranslate = (t, key, fallback) => {
  const translated = t(key);
  return translated && translated !== key ? translated : String(fallback ?? "-");
};

export default ClaimTracking;
